/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* See LICENSE.md in the project root for license terms and full copyright notice.
*--------------------------------------------------------------------------------------------*/

import { useEffect, useState } from "react";
import { useController } from "react-hook-form";
import { DemoIModels, IModelInfo } from "services/SandboxIModels";
import { Checkbox, Select, SelectOption, Text } from "@itwin/itwinui-react";
import { InfoLabel } from "../info-label/InfoLabel";
import { FormField } from "../FormField";

export interface IModelSelectorProps {
  selectedImodels: IModelInfo[];
  saveButtonDisabled?: boolean;
  onIModelsChange: (iModels: IModelInfo[]) => void;
}

export const IModelSelector = ({ name, control }: FormField) => {
  const [lastSelected, setLastSelected] = useState<IModelInfo>(DemoIModels[0]);
  const [options, setOptions] = useState<SelectOption<IModelInfo>[]>([]);
  const { field } = useController({ name, control });

  const onChecked = (option: SelectOption<IModelInfo>, state: SelectOption<IModelInfo>[]) => {
    for (const item of state) {
      if (item.value.name === option.value.name) {
        item.selected = !item.selected;
        setLastSelected(item.value);
        break;
      }
    }
    return [...state];
  };

  const getSelectorName = () => {
    const selectedIModels = options.filter((model) => model.selected);
    let label;

    if (selectedIModels.length === 0)
      label = <Text isMuted>Select iModel(s)</Text>;
    else if (selectedIModels.length === 1)
      label = <>{selectedIModels[0].label}</>;
    else
      label = <>{`${selectedIModels.length} iModel(s) Selected`}</>;

    return label;
  };

  useEffect(() => {
    if (!field.value)
      return;

    //setLastSelected(field.value.length ? field.value[0] : DemoIModels[0]);
    setOptions(DemoIModels.map((iModel): SelectOption<IModelInfo> => {
      return {
        label: iModel.label,
        value: iModel,
        selected: field.value.find((item: any) => item.name === iModel.name) !== undefined,
      };
    }));
  }, [field.value]);

  return (
    <div>
      <InfoLabel label="Assigned iModels" toolTip="Adds an iModel to selectable list in sample widget" />
      <div className="sandbox-metadata-editor-content dropdown-menu-item" data-testid="atp-imodels-dropdown">
        <Select<IModelInfo>
          options={options}
          itemRenderer={(option) => (
            <Checkbox
              label={option.label}
              defaultChecked={option.selected === true}
              onClick={() => setOptions((state) => onChecked(option, state))} // using onClick instead of onChange to keep popup open while user makes multiple selections
            />
          )}
          selectedItemRenderer={getSelectorName}
          value={lastSelected}
          onHide={() => field.onChange((options.filter((item) => item.selected) || []).map((item) => item.value))}
          placeholder="Select iModel(s)"
        />
      </div>
    </div>
  );
};
